import styled from "@emotion/styled"
import { PButtonPure, PFlag, PHeading, PText } from "@porsche-design-system/components-react"
import { spacingStaticLg, spacingStaticSm, spacingStaticXs } from "@porsche-design-system/components-react/emotion"
import type { NewsLanguageCoverage, NewsLanguageStatus, NewsLocaleCoverage } from "@slfinrtl/ui-bff-routes"
import type { ReactNode } from "react"
import { useId, useState } from "react"
import type { MessageDescriptor } from "react-intl"
import { FormattedMessage, useIntl } from "react-intl"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { useMarketplaceName } from "../hooks/useMarketplaceName/useMarketplaceName"
import { getMarketplaceFlagName } from "./getMarketplaceFlagName"
import { newsEditorMessages as messages } from "./newsEditor.messages"

const maxCollapsedItems = 4

const statusMessages: Record<NewsLanguageStatus, MessageDescriptor> = {
	NOT_REVIEWED: messages.languageStatusNotReviewed,
	REVIEWED: messages.languageStatusReviewed,
}

interface NewsLanguageCoverageListProps {
	languageCoverage: NewsLanguageCoverage[]
}

export const NewsLanguageCoverageList = ({ languageCoverage }: NewsLanguageCoverageListProps) => {
	const [isExpanded, setIsExpanded] = useState(false)

	const hiddenCount = languageCoverage.length - maxCollapsedItems
	const visibleItems = isExpanded ? languageCoverage : languageCoverage.slice(0, maxCollapsedItems)

	return (
		<Section>
			<PHeading size="small" weight="semi-bold" tag="h2">
				<FormattedMessage {...messages.languageCoverageTitle} />
			</PHeading>
			<PText color="contrast-medium">
				<FormattedMessage {...messages.languageCoverageSubtitle} />
			</PText>
			<List>
				{visibleItems.map((marketplaceCoverage) => (
					<MarketplaceCoverage key={marketplaceCoverage.marketplaceKey} {...marketplaceCoverage} />
				))}
			</List>
			{hiddenCount > 0 && (
				<PButtonPure
					type="button"
					alignLabel="start"
					icon={isExpanded ? "arrow-head-up" : "arrow-head-down"}
					onClick={() => setIsExpanded(!isExpanded)}
				>
					{isExpanded ? (
						<FormattedMessage {...messages.languageCoverageShowLess} />
					) : (
						<FormattedMessage {...messages.languageCoverageShowMore} values={{ count: hiddenCount }} />
					)}
				</PButtonPure>
			)}
		</Section>
	)
}

const MarketplaceCoverage = ({ locales, marketplaceKey }: NewsLanguageCoverage) => {
	const getMarketplaceName = useMarketplaceName()
	const headingId = useId()
	const marketplaceName = getMarketplaceName(marketplaceKey)

	const flag = <PFlag name={getMarketplaceFlagName(marketplaceKey)} />

	if (locales.length === 1) {
		return (
			<Row>
				<LocaleRow {...locales[0]} flag={flag} marketplaceName={marketplaceName} />
			</Row>
		)
	}

	return (
		<Row $isGroup>
			<Name>
				{flag}
				<PText id={headingId} weight="semi-bold">
					{marketplaceName}
				</PText>
			</Name>
			<Locales aria-labelledby={headingId}>
				{locales.map((localeCoverage) => (
					<LocaleItem key={localeCoverage.locale}>
						<LocaleRow {...localeCoverage} />
					</LocaleItem>
				))}
			</Locales>
		</Row>
	)
}

interface LocaleRowProps extends NewsLocaleCoverage {
	/** the flag and the name of the marketplace, for a marketplace that has no group heading */
	flag?: ReactNode
	marketplaceName?: string
}

const LocaleRow = ({ flag, locale, marketplaceName, status }: LocaleRowProps) => {
	const { formatDisplayName, formatMessage } = useIntl()
	const languageTag = locale.split("-")[0]
	const language = formatDisplayName(languageTag, { type: "language" }) ?? languageTag

	return (
		<LocaleLine>
			<Language>
				{flag}
				<PText>{marketplaceName ? `${marketplaceName} – ${language}` : language}</PText>
			</Language>
			<PText color="contrast-medium">{formatMessage(statusMessages[status])}</PText>
		</LocaleLine>
	)
}

const Section = styled.section`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${spacingStaticSm};
	max-width: 40rem;
	margin-block-start: ${spacingStaticLg};
`

const List = styled.ul`
	align-self: stretch;
	margin: 0;
	padding: 0;
	list-style: none;
`

const Row = styled("li", { shouldForwardProp })<{ $isGroup?: boolean }>`
	display: flex;
	flex-direction: ${({ $isGroup }) => ($isGroup ? "column" : "row")};
	align-items: ${({ $isGroup }) => ($isGroup ? "stretch" : "center")};
	padding-block: ${spacingStaticSm};
`

const Locales = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
`

const LocaleItem = styled.li`
	padding-block-start: ${spacingStaticSm};
`

const LocaleLine = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: space-between;
`

const Name = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${spacingStaticXs};
`

const Language = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${spacingStaticXs};
`
